import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Device + shell state for the whole app.
 *
 * The app measures the real viewport once at boot and then keeps measuring
 * it, instead of relying only on `@media` guesses: `useViewport()` hands
 * every component the live width/height/device class, and the same values
 * are mirrored onto `<html>` as `data-device` / `data-orientation` plus a
 * `--app-h` custom property so CSS can key off them too.
 *
 * `--app-h` matters on phones specifically: `100vh` is the *largest*
 * possible viewport (browser chrome hidden), so a `100vh` app shell is
 * taller than the screen in Safari/Chrome mobile and pushes the composer
 * under the URL bar. `visualViewport.height` is the real, currently-visible
 * height — it also shrinks when the on-screen keyboard opens, which keeps
 * the composer above the keyboard for free.
 *
 * jsdom evaluates none of this, so the state lives in JS and is assertable
 * in a unit test. (This replaces the old `lib/useMediaQuery.ts`, which
 * could answer "is the window narrow?" but not "what device is this, how
 * tall is the *visible* viewport, and is the pointer a finger?")
 */

export type DeviceClass = "phone" | "tablet" | "desktop";

export interface Viewport {
  width: number;
  height: number;
  device: DeviceClass;
  orientation: "portrait" | "landscape";
  /** Coarse pointer (finger) — bigger hit targets, no hover affordances. */
  isTouch: boolean;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Phone *or* tablet: the widths where the sidebar is an off-canvas drawer. */
  isCompact: boolean;
}

/** Breakpoints, in one place so JS and CSS can't drift apart. */
export const PHONE_MAX = 700;
export const TABLET_MAX = 1024;

function classify(width: number, height: number, isTouch: boolean): DeviceClass {
  // A phone in landscape is ~844×390: wide, but still a phone. Classify a
  // touch device by its SHORT side, which is the dimension that actually
  // constrains the layout, so rotating never turns a phone into a
  // "tablet" with tablet-sized chrome on a 390px-tall screen.
  if (isTouch && Math.min(width, height) <= 480) return "phone";
  if (width <= PHONE_MAX) return "phone";
  if (width <= TABLET_MAX) return "tablet";
  // A wide touch screen (iPad landscape, Surface) still wants finger-sized
  // targets and the drawer, but it has room for the roomier layout.
  return isTouch && width <= 1180 ? "tablet" : "desktop";
}

function measure(): Viewport {
  const hasWindow = typeof window !== "undefined";
  const vv = hasWindow ? window.visualViewport : undefined;
  const width = Math.round(vv?.width || (hasWindow ? window.innerWidth : 1280) || 1280);
  const height = Math.round(vv?.height || (hasWindow ? window.innerHeight : 800) || 800);
  // `typeof ... === "function"`, not `"matchMedia" in window`: a test double
  // (and some older embedded webviews) can leave the property defined but
  // not callable, and this runs during the very first render.
  const isTouch =
    hasWindow && typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
  const device = classify(width, height, isTouch);
  return {
    width,
    height,
    device,
    orientation: height >= width ? "portrait" : "landscape",
    isTouch,
    isPhone: device === "phone",
    isTablet: device === "tablet",
    isDesktop: device === "desktop",
    isCompact: device !== "desktop",
  };
}

const ViewportContext = createContext<Viewport | null>(null);

interface ShellState {
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  /** Title shown in the compact top bar — set by whichever view is live. */
  pageTitle: string;
  setPageTitle: (title: string) => void;
  /**
   * Desktop only: the permanent sidebar is collapsed to an icon rail.
   * Distinct from `drawerOpen`, which is the compact off-canvas state —
   * the two never apply at the same viewport, so keeping them separate
   * avoids one flag meaning "open" on a phone and "wide" on a desktop.
   */
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
}

const SIDEBAR_COLLAPSED_KEY = "chatclient.sidebarCollapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

const ShellContext = createContext<ShellState | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [viewport, setViewport] = useState<Viewport>(measure);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState("chatter");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = measure();
        setViewport((prev) =>
          prev.width === next.width &&
          prev.height === next.height &&
          prev.device === next.device &&
          prev.isTouch === next.isTouch
            ? prev
            : next
        );
      });
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    // visualViewport fires on browser-chrome collapse and keyboard open —
    // neither of which raises a plain `resize` on iOS.
    window.visualViewport?.addEventListener("resize", sync);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.visualViewport?.removeEventListener("resize", sync);
    };
  }, []);

  // Mirror the measurement onto the document so plain CSS can react to the
  // *measured* device rather than re-deriving it from its own breakpoints.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute("data-device", viewport.device);
    root.setAttribute("data-orientation", viewport.orientation);
    root.setAttribute("data-pointer", viewport.isTouch ? "coarse" : "fine");
    root.style.setProperty("--app-h", `${viewport.height}px`);
    root.style.setProperty("--app-w", `${viewport.width}px`);
  }, [viewport]);

  // The drawer only exists below the desktop breakpoint; growing past it
  // must never leave a stale "open" state behind the permanent sidebar.
  useEffect(() => {
    if (viewport.isDesktop) setDrawerOpen(false);
  }, [viewport.isDesktop]);

  // An open drawer owns the screen: stop the page behind it from scrolling.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const open = drawerOpen && !viewport.isDesktop;
    document.body.classList.toggle("drawer-locked", open);
    return () => document.body.classList.remove("drawer-locked");
  }, [drawerOpen, viewport.isDesktop]);

  const shell = useMemo<ShellState>(
    () => ({
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      toggleDrawer: () => setDrawerOpen((o) => !o),
      pageTitle,
      setPageTitle,
      sidebarCollapsed,
      toggleSidebarCollapsed: () =>
        setSidebarCollapsed((c) => {
          try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, c ? "0" : "1");
          } catch {
            // a blocked localStorage must not break the toggle itself
          }
          return !c;
        }),
    }),
    [drawerOpen, pageTitle, sidebarCollapsed]
  );

  return (
    <ViewportContext.Provider value={viewport}>
      <ShellContext.Provider value={shell}>{children}</ShellContext.Provider>
    </ViewportContext.Provider>
  );
}

/** Live viewport measurement. Falls back to a one-shot measure so a
 *  component rendered outside the provider (a unit test, Storybook) still
 *  gets sane values instead of throwing. */
export function useViewport(): Viewport {
  const ctx = useContext(ViewportContext);
  const [fallback] = useState(() => (ctx ? null : measure()));
  return ctx ?? (fallback as Viewport);
}

export function useShell(): ShellState {
  const ctx = useContext(ShellContext);
  const [local, setLocal] = useState(false);
  const [localTitle, setLocalTitle] = useState("chatter");
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const toggleSidebarCollapsed = useCallback(() => setLocalCollapsed((c) => !c), []);
  const openDrawer = useCallback(() => setLocal(true), []);
  const closeDrawer = useCallback(() => setLocal(false), []);
  const toggleDrawer = useCallback(() => setLocal((o) => !o), []);
  const standalone = useMemo(
    () => ({
      drawerOpen: local,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      pageTitle: localTitle,
      setPageTitle: setLocalTitle,
      sidebarCollapsed: localCollapsed,
      toggleSidebarCollapsed,
    }),
    [local, localTitle, localCollapsed, openDrawer, closeDrawer, toggleDrawer, toggleSidebarCollapsed]
  );
  return ctx ?? standalone;
}
